import {useEffect, useRef, useState} from "react";
import {User, useIsLoggedIn, useUser} from "UserProvider";
import {AccountApi} from "account/AccountApi";
import {applyAccountToUser} from "account/subscriptionSync";
import {offerEvents} from "upsell/offerEvents";
import {submitReferralCode} from "./ReferralApi";
import {inviteOpenedOnLanding} from "./inviteFunnel";
import {
	captureReferralCode, clearStoredReferralCode, isFinalReferralRefusal, storedReferralCode,
} from "./referralAttribution";
import type {ReferralAttributionStatus} from "./referralLandingOffer";

/**
 * Referral attribution's two browser-side moments (kitecorp/transferify-api-service#433), as a thin React
 * adapter (epic #26): capture the `?ref=` code on ANY landing, and submit it ONCE the visitor is signed in.
 *
 * <p>Since kitecorp/transferify-api-service#1582 the same landing also REPORTS the opening to the invite
 * funnel. It rides here rather than on the landing picker because this is the app's "any landing, any
 * route" seam and an invite link points wherever the referrer pasted it; the rule is
 * `./inviteFunnel.inviteOpenedOnLanding` and the report is a domain event, so this file still knows no
 * transport. A dropped report costs a datapoint and never the attribution: the emitter isolates its
 * listeners and the capture above has already run.
 *
 * <p>Mounted at the app root rather than on the landing page, because the link can point anywhere — a
 * pricing page, a download page, a shared transfer — and an attribution that only worked on `/` would
 * silently lose most of its links.
 *
 * <p>The submit needs no loading state and renders nothing, and it clears the stored code once the
 * server has SETTLED the pairing: a 204, or one of the 4xx refusals that are all terminal for this
 * account (unknown code, self-referral, already attributed, already subscribed). Retrying those on the
 * next page load would re-send a request that can only fail again. Everything else keeps the code: an
 * unreachable server, and equally a 5xx, which says nothing about this pairing at all.
 *
 * <p>Fire-and-forget never meant "ignore the outcome", though: a 204 says the referee grant (api#725) has
 * ALREADY been applied, so the account this app loaded at sign-in is stale the instant it answers. The
 * success branch therefore re-reads `/account` into the user context — without it the billing page kept
 * showing "Free" to a visitor the server had already put on a Basic trial, until a hard reload.
 *
 * <p>Since kitecorp/transferify-ui#2020 it also RETURNS that outcome. The landing picker promises a
 * visitor a gift before they have an account, so it has to know whether the promise survived the
 * signup: an accepted attribution continues into the checkout, a refused one shows the plain notice at
 * list prices and charges nothing, and an unsettled one says nothing at all. Callers that only want the
 * attribution to happen may keep ignoring the value.
 */
export interface ReferralAttributionOutcome {
	status: ReferralAttributionStatus;
	/** The code this outcome is about, or null while none has been submitted. */
	code: string | null;
}

export function useReferralAttribution(): ReferralAttributionOutcome {
	const isLoggedIn = useIsLoggedIn();
	const [user, setUser] = useUser();
	const submitting = useRef(false);
	const [outcome, setOutcome] = useState<ReferralAttributionOutcome>({status: "idle", code: null});

	// Re-read the granted entitlements onto a fresh User clone, via the same additive-safe mapping the
	// session bootstrap and the plan-change refresh use. Read through a ref so the submit effect keeps its
	// `isLoggedIn`-only dependency list — closing over `user` directly would re-arm it on every account
	// change, including the one this very refresh causes.
	const refreshRef = useRef<() => Promise<void>>(() => Promise.resolve());
	refreshRef.current = async () => {
		const account = await AccountApi.getAccount();
		const next = new User(user);
		applyAccountToUser(next, account);
		setUser(next);
	};

	useEffect(() => {
		captureReferralCode(window.location.search, Date.now());
		/*
		 * The referrer's half of the loop, second step, and the reason it is reported HERE: this effect is
		 * already the app's "any landing, any route" seam, and an invite can point at a pricing page, a
		 * download page or a shared transfer as easily as at `/`. Reporting it from the landing picker
		 * instead would count only the arrivals that opened a dialog.
		 *
		 * Named AFTER the capture, deliberately: attribution is the reader's own business and the
		 * measurement is ours, so the order says which one may never be delayed by the other. The transport
		 * routes it — anonymously for the friend who has no account yet, which is most of them, and on the
		 * authenticated beacon for a reader who already had one.
		 *
		 * The latch is the rule's own and lives for the VISIT rather than for this mount, so a reload of the
		 * share link reports nothing; `./inviteFunnel` says why `OFFER_SHOWN`'s per-mount log could not
		 * serve. Recording and deciding are one call for that reason — two would be two chances to drift.
		 */
		if (inviteOpenedOnLanding(window.location.search)) {
			offerEvents.inviteOpened();
		}
	}, []);

	useEffect(() => {
		if (!isLoggedIn || submitting.current) return;
		const code = storedReferralCode(Date.now());
		if (!code) return;
		submitting.current = true;
		setOutcome({status: "submitting", code});
		submitReferralCode(code)
			.then(() => {
				clearStoredReferralCode();
				setOutcome({status: "attributed", code});
				// Settled HERE rather than in the catch below: a failed re-read is not a failed
				// attribution — the grant already landed — and that catch's only job is deciding
				// whether the stored code survives. Its worst case is the behaviour this fix
				// replaced: the account stays stale until the next page load.
				return refreshRef.current().catch(() => undefined);
			})
			.catch((error: unknown) => {
				// A 4xx is the server's final word on this pairing; a 5xx and an unreachable server both say
				// nothing about it, so the code survives and the next visit submits it again. The rule is
				// `isFinalReferralRefusal`, read from here AND by the landing picker that has to tell a
				// refusal from a fault: the two must not answer differently.
				if (isFinalReferralRefusal(error)) {
					clearStoredReferralCode();
					setOutcome({status: "refused", code});
				} else {
					setOutcome({status: "unsettled", code});
				}
			})
			.finally(() => {
				submitting.current = false;
			});
	}, [isLoggedIn]);

	return outcome;
}
